import L from 'leaflet';

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const MERCATOR_MAX_LAT = 85.05112878;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function clampLatitude(value) {
  return clamp(value, -MERCATOR_MAX_LAT, MERCATOR_MAX_LAT);
}

function normalizeDegrees(value) {
  return ((((value + 180) % 360) + 360) % 360) - 180;
}

function normalize360Degrees(value) {
  return ((value % 360) + 360) % 360;
}

function toRadians(value) {
  return value * DEG_TO_RAD;
}

function toDegrees(value) {
  return value * RAD_TO_DEG;
}

function getJulianDay(date) {
  return date.getTime() / 86400000 + 2440587.5;
}

function getSolarPosition(date = new Date()) {
  const julianDay = getJulianDay(date);
  const daysSinceJ2000 = julianDay - 2451545.0;

  const meanLongitude = (((280.46 + 0.9856474 * daysSinceJ2000) % 360) + 360) % 360;
  const meanAnomaly = toRadians((((357.528 + 0.9856003 * daysSinceJ2000) % 360) + 360) % 360);
  const eclipticLongitude = toRadians(meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.02 * Math.sin(2 * meanAnomaly));
  const obliquity = toRadians(23.439 - 0.0000004 * daysSinceJ2000);

  const rightAscension = toDegrees(
    Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLongitude), Math.cos(eclipticLongitude)),
  );
  const declination = toDegrees(Math.asin(Math.sin(obliquity) * Math.sin(eclipticLongitude)));
  const greenwichMeanSiderealTime = 280.46061837 + 360.98564736629 * (julianDay - 2451545.0);

  return {
    declination,
    subsolarLongitude: normalizeDegrees(rightAscension - greenwichMeanSiderealTime),
  };
}

function isNightAt(latitude, longitude, solarPosition) {
  const latitudeRad = toRadians(latitude);
  const declinationRad = toRadians(solarPosition.declination);
  const hourAngleRad = toRadians(normalizeDegrees(longitude - solarPosition.subsolarLongitude));

  const sinAltitude =
    Math.sin(latitudeRad) * Math.sin(declinationRad) +
    Math.cos(latitudeRad) * Math.cos(declinationRad) * Math.cos(hourAngleRad);

  return sinAltitude < 0;
}

function crossProduct(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function normalizeVector(vector) {
  const magnitude = Math.hypot(vector[0], vector[1], vector[2]) || 1;
  return [vector[0] / magnitude, vector[1] / magnitude, vector[2] / magnitude];
}

function splitAntimeridian(points) {
  if (points.length < 2) return points.length ? [points] : [];

  const segments = [];
  let currentSegment = [points[0]];

  for (let i = 1; i < points.length; i += 1) {
    const previousPoint = points[i - 1];
    const point = points[i];
    const longitudeDelta = point.lng - previousPoint.lng;

    if (Math.abs(longitudeDelta) <= 180) {
      currentSegment.push(point);
      continue;
    }

    const crossingLongitude = longitudeDelta > 180 ? -180 : 180;
    const oppositeLongitude = crossingLongitude === 180 ? -180 : 180;
    const adjustedLongitude = longitudeDelta > 180 ? point.lng - 360 : point.lng + 360;
    const interpolationRatio = (crossingLongitude - previousPoint.lng) / (adjustedLongitude - previousPoint.lng);
    const crossingLatitude = previousPoint.lat + (point.lat - previousPoint.lat) * interpolationRatio;

    currentSegment.push({
      lat: crossingLatitude,
      lng: crossingLongitude,
    });
    segments.push(currentSegment);

    currentSegment = [
      {
        lat: crossingLatitude,
        lng: oppositeLongitude,
      },
      point,
    ];
  }

  if (currentSegment.length > 1) {
    segments.push(currentSegment);
  }

  return segments;
}

function getTerminatorSegments(solarPosition, pointCount = 720) {
  const declinationRad = toRadians(solarPosition.declination);
  const subsolarLongitudeRad = toRadians(solarPosition.subsolarLongitude);
  const sunVector = [
    Math.cos(declinationRad) * Math.cos(subsolarLongitudeRad),
    Math.cos(declinationRad) * Math.sin(subsolarLongitudeRad),
    Math.sin(declinationRad),
  ];
  const referenceVector = Math.abs(sunVector[2]) < 0.99 ? [0, 0, 1] : [0, 1, 0];
  const basisU = normalizeVector(crossProduct(referenceVector, sunVector));
  const basisV = normalizeVector(crossProduct(sunVector, basisU));
  const points = [];

  for (let i = 0; i <= pointCount; i += 1) {
    const angle = (2 * Math.PI * i) / pointCount;
    const x = basisU[0] * Math.cos(angle) + basisV[0] * Math.sin(angle);
    const y = basisU[1] * Math.cos(angle) + basisV[1] * Math.sin(angle);
    const z = basisU[2] * Math.cos(angle) + basisV[2] * Math.sin(angle);

    points.push({
      lat: clampLatitude(toDegrees(Math.asin(clamp(z, -1, 1)))),
      lng: normalizeDegrees(toDegrees(Math.atan2(y, x))),
    });
  }

  return splitAntimeridian(points);
}

function convertIntervalToMapRanges(start, end) {
  if (end - start >= 360) {
    return [[-180, 180]];
  }

  const intervals = [];
  if (start < 180 && end > 180) {
    intervals.push([start, 180]);
    intervals.push([-180, end - 360]);
    return intervals;
  }
  if (start >= 180) {
    intervals.push([start - 360, end - 360]);
    return intervals;
  }

  intervals.push([start, end]);
  return intervals;
}

function getNightIntervalsForLatitude(latitude, solarPosition) {
  const latitudeRad = toRadians(latitude);
  const declinationRad = toRadians(solarPosition.declination);
  const threshold = -Math.tan(latitudeRad) * Math.tan(declinationRad);

  if (threshold <= -1) {
    return [];
  }
  if (threshold >= 1) {
    return [[-180, 180]];
  }

  const daylightHalfWidth = toDegrees(Math.acos(clamp(threshold, -1, 1)));
  const daylightStart = normalize360Degrees(solarPosition.subsolarLongitude - daylightHalfWidth);
  const daylightEnd = normalize360Degrees(solarPosition.subsolarLongitude + daylightHalfWidth);

  if (daylightStart <= daylightEnd) {
    return [
      ...convertIntervalToMapRanges(0, daylightStart).filter(([start, end]) => end - start > 0.0001),
      ...convertIntervalToMapRanges(daylightEnd, 360).filter(([start, end]) => end - start > 0.0001),
    ];
  }

  return convertIntervalToMapRanges(daylightEnd, daylightStart).filter(([start, end]) => end - start > 0.0001);
}

const DayNightTerminatorLayer = L.Layer.extend({
  options: {
    pointCount: 720,
    rowStep: 2,
    fillStyle: 'rgba(6, 18, 34, 0.18)',
    lineColor: 'rgba(255, 198, 107, 0.9)',
    lineGlowColor: 'rgba(255, 198, 107, 0.22)',
    lineWidth: 1.5,
    updateIntervalMs: 60000,
  },

  onAdd(map) {
    this._map = map;
    this._canvas = L.DomUtil.create('canvas', 'leaflet-day-night-overlay');
    this._canvas.style.position = 'absolute';
    this._canvas.style.pointerEvents = 'none';
    this._canvas.style.zIndex = '400';
    this._context = this._canvas.getContext('2d');

    map.getPanes().overlayPane.appendChild(this._canvas);

    this._redraw = this._redraw.bind(this);
    map.on('moveend zoomend resize viewreset', this._redraw);
    this._interval = globalThis.setInterval(this._redraw, this.options.updateIntervalMs);

    this._redraw();
  },

  onRemove(map) {
    map.off('moveend zoomend resize viewreset', this._redraw);
    if (this._interval) {
      globalThis.clearInterval(this._interval);
      this._interval = null;
    }

    if (this._canvas?.parentNode) {
      this._canvas.parentNode.removeChild(this._canvas);
    }

    this._canvas = null;
    this._context = null;
    this._map = null;
  },

  _drawPolyline(points) {
    if (points.length < 2) return;

    const ctx = this._context;
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    points.slice(1).forEach(point => {
      ctx.lineTo(point[0], point[1]);
    });
  },

  _redraw() {
    if (!this._map || !this._canvas || !this._context) return;

    const map = this._map;
    const size = map.getSize();
    const pad = 40;
    const topLeft = map.containerPointToLayerPoint([-pad, -pad]);

    this._canvas.width = size.x + pad * 2;
    this._canvas.height = size.y + pad * 2;
    L.DomUtil.setPosition(this._canvas, topLeft);

    const ctx = this._context;
    const solarPosition = getSolarPosition(new Date());
    const rowStep = Math.max(this.options.rowStep, 2);
    const terminatorSegments = getTerminatorSegments(solarPosition, this.options.pointCount);

    const canvasW = size.x + pad * 2;
    const canvasH = size.y + pad * 2;
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Compute world pixel width to draw at multiple offsets
    const worldLeft = map.latLngToContainerPoint([0, -180]).x + pad;
    const worldRight = map.latLngToContainerPoint([0, 180]).x + pad;
    const worldWidth = worldRight - worldLeft;

    // Determine how many world copies are visible (shared by shade + line)
    const offsets = [0];
    if (worldWidth > 0) {
      for (let dx = -worldWidth; worldLeft + dx + worldWidth > 0; dx -= worldWidth) offsets.push(dx);
      for (let dx = worldWidth; worldLeft + dx < size.x; dx += worldWidth) offsets.push(dx);
    }

    // Draw night shade
    ctx.fillStyle = this.options.fillStyle;
    for (let y = 0; y <= canvasH; y += rowStep) {
      const containerY = y - pad;
      const latitude = clampLatitude(map.containerPointToLatLng([0, containerY]).lat);
      const nightIntervals = getNightIntervalsForLatitude(latitude, solarPosition);

      nightIntervals.forEach(([startLongitude, endLongitude]) => {
        const startX = map.latLngToContainerPoint([latitude, startLongitude]).x + pad;
        const endX = map.latLngToContainerPoint([latitude, endLongitude]).x + pad;

        offsets.forEach(dx => {
          const left = Math.max(0, Math.min(startX + dx, endX + dx));
          const right = Math.min(canvasW, Math.max(startX + dx, endX + dx));
          if (right > left) {
            ctx.fillRect(left, y, right - left, rowStep + 1);
          }
        });
      });
    }

    // Draw terminator line
    terminatorSegments.forEach(segment => {
      const baseSegment = segment.map(point => {
        const projectedPoint = map.latLngToContainerPoint([point.lat, point.lng]);
        return [projectedPoint.x + pad, projectedPoint.y + pad];
      });

      offsets.forEach(dx => {
        const projectedSegment = dx === 0 ? baseSegment : baseSegment.map(([x, y]) => [x + dx, y]);

        this._drawPolyline(projectedSegment);
        ctx.strokeStyle = this.options.lineGlowColor;
        ctx.lineWidth = this.options.lineWidth + 3;
        ctx.stroke();

        this._drawPolyline(projectedSegment);
        ctx.strokeStyle = this.options.lineColor;
        ctx.lineWidth = this.options.lineWidth;
        ctx.stroke();
      });
    });
  },
});

export function createDayNightTerminatorLayer(options = {}) {
  return new DayNightTerminatorLayer(options);
}
